import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { NavigationStart } from '@angular/router';
import {
    IAddToBasketErrorEvent,
    ProductAddToBasketComponentDef
} from '@df/catalog-ui/product/add-to-basket/product-add-to-basket.component.def';
import { IProductConfigurableSelection, IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { DfDomModule } from '@df/dom/df-dom.module';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ItemQtyComponent } from 'projects/app-default/src/app/basket/item-qty.component';
import { EGtmAddToCartVia, LocalGtmService } from 'projects/app-default/src/app/module/gtm/gtm.service.decorator';
import { SizebayService } from 'projects/app-default/src/app/module/sizebay/sizebay.service';
import { IfNotTradeStoreDirective } from 'projects/app-default/src/app/multisite/trade/if-not-trade-store.directive';
import { ActionComponent } from 'projects/app-default/src/app/ui/api/action.component';
import { LabelPaneComponent } from 'projects/app-default/src/app/ui/display/label-pane/label-pane.component';
import { ResultComponent } from 'projects/app-default/src/app/ui/resut/result.component';
import { BottomPanelComponent } from 'projects/app-default/src/app/ui/widget/bottom-panel/bottom-panel.component';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LocalProduct } from '../../../model/product/product.decorator';
import { ConfigurableOptionsErrorService } from '../configurable-options/configurable-options-error/configurable-options-error.service';
import { ADD_TO_BASKET_WITHOUT_OPTION_EVENT } from '../configurable-options/dropdown/product-configurable-option-dropdown.component';
import { ATTR_WITH_DROPDOWN, MULTIPLE_SELECTOR_GROUP } from '../configurable-options/product-configurable-options.component';
import { NNotifyMeModal } from '../notify-me-modal/product-notify-me-modal';
import { ProductViewWishlistToggleComponent } from '../product-view-wishlist-toggle/product-view-wishlist-toggle.component';

@Component({
    selector: 'product-view-add-to-basket',
    templateUrl: 'product-view-add-to-basket.html',
    standalone: true,
    imports: [
        TranslationModule,
        ItemQtyComponent,
        BottomPanelComponent,
        DfUiModule,
        DfDomModule,
        NgTemplateOutlet,
        LabelPaneComponent,
        ActionComponent,
        ProductViewWishlistToggleComponent,
        IfNotTradeStoreDirective,
        ResultComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewAddToBasketComponent
    extends ProductAddToBasketComponentDef<LocalProduct<IProductDataV2>>
    implements OnInit, OnChanges
{
    selectedQty?: number;
    @Input()
    gtmAddToCartVia?: EGtmAddToCartVia;

    @Input()
    hideRetailQty?: boolean;

    @Input()
    productConfigurableSelection?: IProductConfigurableSelection;

    private attributeLabelPipeService = inject(AttributeLabelPipeService);
    private translationService = inject(TranslationService);
    private client = inject(Client);
    private gtmService = inject(LocalGtmService);
    configurableOptionsErrorService = inject(ConfigurableOptionsErrorService);
    private scrollToService = inject(ScrollToService);
    private dfEventService = inject(DfEventService);
    protected dfModalService = inject(DfModalService);
    private readonly UNSELECTED_ATTR_WITHOUT_ERROR = [...ATTR_WITH_DROPDOWN, ...MULTIPLE_SELECTOR_GROUP];
    private sizebayService = inject(SizebayService);
    private configurableError = {
        shouldShow: false,
        attribute: undefined
    };
    showError = false;
    showErrorSubject = new BehaviorSubject<boolean>(false);
    override ngOnInit(): void {
        super.ngOnInit();
        this.app.customer.model.load().then(() => this.updateSelectedQty(true));
        this.subscriptions.add(this.app.customer.basket.subscribe(() => this.updateSelectedQty()));
        // reset quantity on route change
        this.subscriptions.add(
            this.app.meta.routerEvents.pipe(filter(event => event instanceof NavigationStart)).subscribe(() => {
                this.qty = 1;
            })
        );
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['product']) {
            this.resetConfigurableError();
            this.configurableOptionsErrorService.error.next(this.configurableError);
            this.updateSelectedQty(true);
        }
        if (changes['productConfigurableSelection']) {
            this.detectChanges();
        }
    }

    get notSelectedAttr(): string[] {
        return this.product?.configurableAttributeCodes?.filter(attr => !this.productConfigurableSelection?.[attr]) || [];
    }
    /**
     * Retrieves error message for missing configurations
     */
    get configurationWarning(): string | undefined {
        if (this.product?.isConfigurable) {
            const attrs = this.product.configurableAttributeCodes;
            if (attrs && this.notSelectedAttr.length > 0) {
                if (this.notSelectedAttr?.every(attr => this.UNSELECTED_ATTR_WITHOUT_ERROR.includes(attr))) {
                    return undefined;
                } else {
                    return this.translationService.transform('Select %', this.attributeLabelPipeService.execute(this.notSelectedAttr[0]));
                }
            }
        }
        return undefined;
    }
    /**
     * Checks if button should be disabled
     */
    get isDisabled(): boolean {
        return !this.product?.isConfigurable && !this.configurationWarning && !this.isSalable && !this.product?.isNotifyMe;
    }

    get buttonCopy() {
        const product = this.product as LocalProduct<IProductDataV3>;
        return (
            this.configurationWarning ||
            this.translationService.transform(
                this.product?.isConfigurable
                    ? 'Add to bag'
                    : this.product?.isNotifyMe
                      ? 'Notify Me'
                      : product.isBackOrder
                        ? 'Add back order to basket'
                        : product.isPreOrder
                          ? 'Pre-order'
                          : !this.isSalable
                            ? 'Out of stock'
                            : 'Add to bag'
            )
        );
    }
    get warning(): string | undefined {
        if (this.maxQty > 0 && (!this.isSalable || this.maxQty < 6)) {
            return this.translationService.transform('Only % left in stock', this.maxQty);
        }
        return undefined;
    }
    onQtyChange() {
        this.detectChanges();
    }
    override _submit() {
        this.resetConfigurableError();
        if (this.configurationWarning) {
            this.configurableError.shouldShow = true;
            this.configurableOptionsErrorService.error.next(this.configurableError);
            if (this.client.isS || this.client.isM) {
                this.scrollToService.scrollTo('#pdpConfigurableOptions', { offset: 100 });
            }
        } else if (this.product?.isConfigurable && this.notSelectedAttr.length > 0) {
            // logic for open last configurable option dropdown and submit
            const openUnselectedDropdown = () => {
                this.dfEventService.dispatch({
                    id: ADD_TO_BASKET_WITHOUT_OPTION_EVENT,
                    data: {
                        attrCode: this.UNSELECTED_ATTR_WITHOUT_ERROR.find(attr => this.notSelectedAttr.includes(attr)) || ''
                    },
                    action: async data => {
                        // change product to product selected by last config option
                        // if you know how to do it better please do
                        if (data.product?.isPos) {
                            return;
                        }
                        if (data.product && !this.notSelectedAttr.length) {
                            this.product = data.product as LocalProduct<IProductDataV3>;
                            // recurency call to check if we dont have any warnings
                            this._submit();
                        } else if (this.UNSELECTED_ATTR_WITHOUT_ERROR.find(attr => this.notSelectedAttr.includes(attr))) {
                            // open next unselected multiple selector dropdown
                            openUnselectedDropdown();
                        }
                    }
                });
            };
            openUnselectedDropdown();
        } else if (this.product?.isNotifyMe) {
            this.dfModalService.open(undefined, NNotifyMeModal.BLOCK_ID, undefined, {
                product: this.product
            });
        } else {
            super._submit();
        }
    }
    private updateSelectedQty(force = false): void {
        if (this.product) {
            const minQty = (this.product.canUseIncrementAsMinQty() ? this.product.increment : this.product.minQty) || 1;
            if (force || !this.selectedQty || minQty > this.selectedQty) {
                this.selectedQty = minQty;
            }
        } else {
            this.selectedQty = 1;
        }
        this.detectChanges();
    }
    private resetConfigurableError() {
        this.configurableError = {
            shouldShow: false,
            attribute: undefined
        };
    }
    protected override onError(errorEvent: IAddToBasketErrorEvent): void {
        this.onAddToBasketError(errorEvent);
        super.onError(errorEvent);
    }

    private onAddToBasketError(event: IAddToBasketErrorEvent): void {
        const currentId = this.product?.id;
        if (currentId) {
            const idsInRequest = event?.request.map(i => i.id);
            if (idsInRequest?.indexOf(currentId) > -1) {
                this.selectedQty = this.product!.validateQty(this.selectedQty || 1);
                this.detectChanges();
            }
        }
    }
    protected override onSuccess(result?: any) {
        if (this.product?.data?.sku) {
            this.sizebayService.addToBasketCall([this.product?.parent?.data?.sku || this.product?.data?.sku]);
        }
        this.gtmService.gtmAddToCartBeEvent(result, this.gtmAddToCartVia || EGtmAddToCartVia.pdp);
        return super.onSuccess(result);
    }
}
